import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Users, DollarSign, Video, ShoppingBag, BookOpen, Check, X, Upload, TrendingUp, Crown, Lock, Unlock, Settings, RefreshCw, Globe, Edit3, FileSpreadsheet, AlertCircle, Download, Youtube, Plus, Layers, Calendar, Flag, Star, Trash2, Gift, Ban, UserX, UserCheck, CheckCircle, ShieldCheck, Clock, PartyPopper, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Founder Membership tab.
 *
 * Lifted out of AdminPanel.js during the Phase 2 frontend split (Wave 4).
 * Pure presentational — receives parent state + handlers via props.
 */
export default function FounderMembershipTab(props) {
  const { user, navigate, getAuthHeader, fetchAllData, fileInputRef, users, setUsers, userSearch, setUserSearch, filteredUsers, pendingFounders, founderSettings, setFounderSettings, founderSeatsRemaining, updateFounderSettings, toggleFounderEnrollment, toggleFoundingMember, suspendUser, archiveUser, permanentlyDeleteUser, changeUserTier, sales, pendingSales, pendingListingSales, handleVerifySale, handleVerifyListingSale, handleUnverifyListingSale, handleCancelListingSale, commissionDashboard, promoCodes, newPromo, setNewPromo, createPromoCode, deletePromoCode, promoToDelete, setPromoToDelete, potData, setPotData, updatePot, advertisers, pendingVideos, pendingLibraryVideos, handleBatchApproveVideos, handleVideoReview, handleLibraryVideoReview, moveDownlineData, setMoveDownlineData, featuredMembers, selectedUserToFeature, setSelectedUserToFeature, featuredText, setFeaturedText, handleAddFeaturedMember, handleRemoveFeaturedMember, tierFeatures, editingTier, setEditingTier, editFeatures, setEditFeatures, startEditingTier, saveTierFeatures, customTiers, showTierForm, setShowTierForm, newTier, setNewTier, tierFeaturesInput, setTierFeaturesInput, handleCreateTier, handleDeleteTier, pendingServices, flaggedServices, handleApproveService, handleFlagService, handleDeleteService, edirectoryStatus, syncingEdir, syncEdirectory, importedTransactions, importing, importResult, handleFileSelect, downloadSampleCSV } = props;

  return (
    <div className="space-y-4" data-testid="founder-tab">
      <h2 className="text-2xl font-playfair font-bold text-foreground mb-4 flex items-center gap-2">
                    <Crown className="w-6 h-6 text-amber-500" /> Founder Membership Controls
                  </h2>
            
                  {/* Status Card */}
                  <div className="wm-card p-6 rounded-lg border-2 border-amber-500/30">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-2">Enrollment Status</h3>
                        <div className="flex items-center gap-4">
                          <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${
                            founderSettings.founder_is_open 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {founderSettings.founder_is_open ? (
                              <><Unlock className="w-4 h-4" /> Open for Enrollment</>
                            ) : (
                              <><Lock className="w-4 h-4" /> Closed</>
                            )}
                          </span>
                          <span className="text-muted-foreground">
                            {founderSettings.founder_seats_used} / {founderSettings.founder_seat_limit} seats filled
                          </span>
                        </div>
                      </div>
                      <Button
                        onClick={toggleFounderEnrollment}
                        className={founderSettings.founder_is_open 
                          ? 'bg-red-600 hover:bg-red-700' 
                          : 'bg-green-600 hover:bg-green-700'
                        }
                        data-testid="toggle-founder-enrollment-btn"
                      >
                        {founderSettings.founder_is_open ? (
                          <><Lock className="w-4 h-4 mr-2" /> Close Enrollment</>
                        ) : (
                          <><Unlock className="w-4 h-4 mr-2" /> Open Enrollment</>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Seats Progress */}
                  <div className="wm-card p-6 rounded-lg">
                    <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                      <h3 className="text-lg font-semibold text-foreground">Founder Seats</h3>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const dry = await axios.post(
                                `${API_URL}/api/admin/backfill-listings?dry_run=true`,
                                {},
                                { headers: getAuthHeader() }
                              );
                              const { topped_up, checked, already_ok, changes } = dry.data;
                              if (topped_up === 0) {
                                toast.success(`All ${checked} paid users already have their listings quota.`);
                                return;
                              }
                              const sample = changes.slice(0, 5).map(c => `• ${c.email} (${c.tier}): ${c.from_total} → ${c.to_total}`).join('\n');
                              const more = changes.length > 5 ? `\n…and ${topped_up - 5} more` : '';
                              const ok = window.confirm(
                                `Top up listings for ${topped_up} users?\n\n` +
                                `Already OK: ${already_ok}\nWill be updated: ${topped_up}\n\n` +
                                `Sample:\n${sample}${more}\n\nSafe — never reduces anyone's total.`
                              );
                              if (!ok) return;
                              const run = await axios.post(
                                `${API_URL}/api/admin/backfill-listings?dry_run=false`,
                                {},
                                { headers: getAuthHeader() }
                              );
                              toast.success(`Topped up ${run.data.topped_up} users. ${run.data.already_ok} were already OK.`);
                              fetchAllData();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Could not backfill listings.');
                            }
                          }}
                          className="text-xs"
                          data-testid="backfill-listings-btn"
                          title="Top every paid-tier member up to their tier's listings quota (Founder=50k, Growth=25k, Ascend=1k). Never reduces."
                        >
                          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Backfill listings
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const res = await axios.post(
                                `${API_URL}/api/admin/founder-settings/reconcile-seats`,
                                {},
                                { headers: getAuthHeader() }
                              );
                              toast.success(`Seats reconciled: ${res.data.founder_seats_used} active Founder(s).`);
                              fetchAllData();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Could not reconcile seats.');
                            }
                          }}
                          className="text-xs"
                          data-testid="reconcile-founder-seats-btn"
                          title="Recount seats from the users table. Use this if the counter looks wrong after manual edits."
                        >
                          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reconcile seats
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Seats Used</span>
                        <span className="text-amber-500 font-bold">{founderSettings.founder_seats_used}</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-4 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-yellow-600 rounded-full transition-all duration-500"
                          style={{ width: `${(founderSettings.founder_seats_used / founderSettings.founder_seat_limit) * 100}%` }}
                          data-testid="founder-seats-progress"
                        />
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-green-400">{founderSeatsRemaining} seats remaining</span>
                        <span className="text-muted-foreground">{founderSettings.founder_seat_limit} total limit</span>
                      </div>
                    </div>
                  </div>

                  {/* Settings Form */}
                  <div className="wm-card p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Settings className="w-5 h-5" /> Seat Configuration
                    </h3>
                    <form onSubmit={updateFounderSettings} className="space-y-4" data-testid="founder-settings-form">
                      <div>
                        <Label htmlFor="seatLimit" className="text-foreground">Maximum Founder Seats</Label>
                        <Input
                          id="seatLimit"
                          type="number"
                          min="1"
                          value={founderSettings.founder_seat_limit}
                          onChange={(e) => setFounderSettings({ 
                            ...founderSettings, 
                            founder_seat_limit: parseInt(e.target.value) || 1 
                          })}
                          className="mt-1 bg-input border-border text-foreground"
                          data-testid="founder-seat-limit-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Set the maximum number of Founder memberships available
                        </p>
                      </div>
                      <Button
                        type="submit"
                        className="bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid="save-founder-settings-btn"
                      >
                        Save Settings
                      </Button>
                    </form>
                  </div>

                  {/* Pricing Info */}
                  <div className="wm-card p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-foreground mb-4">Founder Pricing (Reference)</h3>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div className="p-4 rounded bg-slate-800/50">
                        <p className="text-muted-foreground">Monthly Fee</p>
                        <p className="text-2xl font-bold text-foreground">$149.99</p>
                      </div>
                      <div className="p-4 rounded bg-slate-800/50">
                        <p className="text-muted-foreground">One-Time Initiation</p>
                        <p className="text-2xl font-bold text-amber-500">$500.00</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-4">
                      Pricing is configured in Stripe. Contact support to modify pricing.
                    </p>
                  </div>
    </div>
  );
}
